import React from "react";
import { useNavigate } from "react-router-dom";
import { StatusPill, SplitButton, ProgressBar, Tooltip } from "components";
import {
  getDisplayStatus,
  computeCompletion,
  computePlayProgress,
  PUBLISH_STATUS,
} from "../../lifecycle";
import { metaParts, touchedAgo } from "../dashboardUtils";
import { PlayIcon, CheckIcon, PencilIcon } from "./icons";

const PRIMARY_ICON = {
  play: <PlayIcon size={12} />,
  check: <CheckIcon size={12} />,
  pencil: <PencilIcon size={12} />,
};

// Match the grid cards: Launch/Resume are the loud gradient CTA, Mark Ready is
// solid primary, everything else stays a quiet secondary.
const VARIANT_BY_STATUS = {
  ready: "cta",
  "in-progress": "cta",
  awaiting: "primary",
};

/**
 * The "Clues" cell: clue-WRITING progress while a game is being authored
 * (draft/ready), switching to clue-PLAYING progress once it's live or done
 * (in-progress/played). The label spells out which, so a launched game reading
 * "12/60 played" isn't confused with authoring (§9.1).
 */
const ClueCell = ({ game, status }) => {
  const isLive =
    status === PUBLISH_STATUS.IN_PROGRESS || status === PUBLISH_STATUS.PLAYED;

  if (isLive) {
    const { played, total, percentage } = computePlayProgress(game);
    return (
      <div className="da-q-progress">
        <ProgressBar value={percentage} tone="accent" height={5} />
        <span className="da-q-num">
          {played}
          <span className="da-dim">/{total} played</span>
        </span>
      </div>
    );
  }

  const { written, total } = metaParts(game);
  const { percentage } = computeCompletion(game);
  if (percentage >= 100) {
    return (
      <div className="da-q-progress">
        <span className="da-q-complete">
          <CheckIcon size={12} /> {written}/{total} written
        </span>
      </div>
    );
  }
  return (
    <div className="da-q-progress">
      <ProgressBar value={percentage} tone={percentage < 60 ? "warn" : "accent"} height={5} />
      <span className="da-q-num">
        {written}
        <span className="da-dim">/{total} written</span>
      </span>
    </div>
  );
};

/** One row of the queue table (PRD §9). Row click → Game Home. */
const QueueRow = ({ game, actions }) => {
  const navigate = useNavigate();
  const status = getDisplayStatus(game);
  const { roundsLabel, written } = metaParts(game);
  const primary = actions?.primary;
  const goHome = () => navigate(`/manage/game/${game.id}`);

  return (
    <div
      className="da-q-row"
      role="row"
      tabIndex={0}
      onClick={goHome}
      onKeyDown={(e) => {
        if (e.key === "Enter") goHome();
      }}
    >
      <div className="da-q-title">
        <span className="da-q-name">{game.name || "Untitled game"}</span>
        <span className="da-q-sub">
          {roundsLabel} · {written} clues
        </span>
      </div>
      <div className="da-q-status">
        <StatusPill status={status} size="sm" />
      </div>
      <Tooltip title={touchedAgo(game) || "No activity yet"}>
        <div className="da-q-touched">{touchedAgo(game) || "—"}</div>
      </Tooltip>
      <ClueCell game={game} status={status} />
      <div className="da-q-actions" onClick={(e) => e.stopPropagation()}>
        {primary && (
          <SplitButton
            variant={VARIANT_BY_STATUS[status] || "secondary"}
            size="sm"
            startIcon={PRIMARY_ICON[primary.icon]}
            onClick={primary.onClick}
            items={actions.menuItems}
          >
            {primary.label}
          </SplitButton>
        )}
      </div>
    </div>
  );
};

export default QueueRow;
